import React from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import DialogTextField from "components/common/inputs/DialogTextField.js";

import BaseDataEntryDialog from "./BaseDataEntryDialog";

const global_configs = require("store/data/global_configs.json");
const MAX_LEN = global_configs["data"]["scenario-name"]["max-length"];

export default function ScenarioNameDialog(props) {
  const { open } = props;

  const [textEntry, setTextEntry] = React.useState("");
  const [textChanged, setTextChanged] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setTextEntry("");
    setTextChanged(false);
  }, [open]);

  const trimmedValue = textEntry.trim();
  const hasValue = Boolean(trimmedValue);
  const isTooLong = trimmedValue.length > MAX_LEN;
  const isValid = hasValue && !isTooLong;

  const handleSave = () => {
    if (!isValid) {
      setTextChanged(true);
      return;
    }
    props.handleSaveAndClose(trimmedValue);
  };

  const helperText = (() => {
    if (textChanged && !hasValue) {
      return "Scenario name is required.";
    }
    if (textChanged && isTooLong) {
      return `Maximum ${MAX_LEN} characters allowed.`;
    }
    return `${trimmedValue.length}/${MAX_LEN}`;
  })();

  const showError = (textChanged && !hasValue) || (textChanged && isTooLong);

  return (
    <BaseDataEntryDialog
      open={open}
      onClose={props.handleCancel}
      title="Name your scenario"
      description="This name will be used in Saved Scenarios."
      maxWidth="xs"
      titleId="scenario-name-dialog-title"
      descriptionId="scenario-name-dialog-description"
      actions={
        <>
          <Button
            onClick={props.handleCancel}
            variant="outlined"
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                borderColor: "primary.extraDark",
                backgroundColor: "primary.extraLight",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isValid}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.25,
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <DialogTextField
          label="Scenario name"
          value={textEntry}
          onChange={(event) => {
            setTextEntry(event.target.value);
            setTextChanged(true);
          }}
          // Dialog-scoped autofocus: focusing the primary input when a modal
          // opens follows the WAI-ARIA dialog pattern (MUI traps focus).
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          fullWidth
          size="small"
          inputProps={{ maxLength: MAX_LEN }}
          error={Boolean(showError)}
          helperText={
            <Typography
              component="span"
              variant="caption"
              sx={{ color: showError ? "error.text" : "text.secondary" }}
            >
              {helperText}
            </Typography>
          }
        />
      </Box>
    </BaseDataEntryDialog>
  );
}

ScenarioNameDialog.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleSaveAndClose: PropTypes.func.isRequired,
};
